import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/theme/ThemeProvider";
import { NetworkProvider } from "@/context/NetworkContext";
import Protected from "@/components/Protected";
import AppShell from "@/components/AppShell";
import Placeholder from "@/pages/Placeholder";
import Login from "@/pages/Login";
import CounterSignin from "@/pages/CounterSignin";
import StyleGuide from "@/pages/StyleGuide";
import Today from "@/pages/Today";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import ProductForm from "@/pages/ProductForm";
import AddProducts from "@/pages/AddProducts";
import ImportProducts from "@/pages/ImportProducts";
import Counter from "@/pages/Counter";
import StorefrontStudio from "@/pages/StorefrontStudio";
import Deals from "@/pages/Deals";

const Storefront = lazy(() => import("@/storefront/StorefrontApp"));
import DealBuilder from "@/pages/DealBuilder";
import ShopPicker from "@/pages/ShopPicker";
import Connections from "@/pages/Connections";
import PlatformConnections from "@/pages/PlatformConnections";
import McpServers from "@/pages/McpServers";
import ToolConnections from "@/pages/ToolConnections";
import SetupWizard from "@/pages/SetupWizard";
import Posts from "@/pages/Posts";
import Numbers from "@/pages/Numbers";
import Categories from "@/pages/Categories";
import Payouts from "@/pages/Payouts";
import AiCost from "@/pages/AiCost";
import Loyalty from "@/pages/Loyalty";
import Reviews from "@/pages/Reviews";
import Overview from "@/pages/Overview";
import Assistants from "@/pages/Assistants";
import Alerts from "@/pages/Alerts";
import EmailSetup from "@/pages/EmailSetup";

// Destinations without a real screen yet show a designed placeholder.
const PLACEHOLDERS = [
  ["/customers", "Customers"],
  ["/payments", "Payments"],
  ["/team", "Team"], ["/shop-settings", "Shop settings"],
  ["/shops", "All shops"], ["/setting-up", "Setting up"], ["/partners", "Partners"],
  ["/plans", "Plans and rates"],
  ["/activity", "Activity log"],
];

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <NetworkProvider>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/counter-signin" element={<CounterSignin />} />
                <Route path="/styleguide" element={<StyleGuide />} />
                <Route path="/s/:shopId" element={<Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}><Storefront /></Suspense>} />

                {/* Persistent shell — only the content area changes on navigation. */}
                <Route element={<Protected><AppShell /></Protected>}>
                  <Route path="/today" element={<Today />} />
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/orders/:orderId" element={<OrderDetail />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/new" element={<ProductForm />} />
                  <Route path="/products/add" element={<AddProducts />} />
                  <Route path="/products/import" element={<ImportProducts />} />
                  <Route path="/products/:productId" element={<ProductDetail />} />
                  <Route path="/products/:productId/edit" element={<ProductForm />} />
                  <Route path="/counter" element={<Counter />} />
                  <Route path="/shop-page" element={<StorefrontStudio />} />
                  <Route path="/deals" element={<Deals />} />
                  <Route path="/deals/new" element={<DealBuilder />} />
                  <Route path="/deals/:dealId/edit" element={<DealBuilder />} />
                  <Route path="/connections" element={<Connections />} />
                  <Route path="/platform-connections" element={<PlatformConnections />} />
                  <Route path="/mcp-servers" element={<McpServers />} />
                  <Route path="/tool-connections" element={<ToolConnections />} />
                  <Route path="/setup" element={<SetupWizard />} />
                  <Route path="/posts" element={<Posts />} />
                  <Route path="/numbers" element={<Numbers />} />
                  <Route path="/categories" element={<Categories />} />
                  <Route path="/payouts" element={<Payouts />} />
                  <Route path="/ai-cost" element={<AiCost />} />
                  <Route path="/loyalty" element={<Loyalty />} />
                  <Route path="/reviews" element={<Reviews />} />
                  <Route path="/overview" element={<Overview />} />
                  <Route path="/assistants" element={<Assistants />} />
                  <Route path="/alerts" element={<Alerts />} />
                  <Route path="/email" element={<EmailSetup />} />
                  <Route path="/pick-shop" element={<ShopPicker />} />
                  {PLACEHOLDERS.map(([path, title]) => (
                    <Route key={path} path={path} element={<Placeholder title={title} />} />
                  ))}
                </Route>

                <Route path="/" element={<Navigate to="/today" replace />} />
                <Route path="*" element={<Navigate to="/today" replace />} />
              </Routes>
            </NetworkProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
